'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import { CONTACT_EMAIL, LEGAL_ENTITY_NAME, LEGAL_ENTITY_NIPT, LEGAL_ENTITY_ADDRESS } from '@/lib/legal/legalConfig';

interface LegalSection {
  heading: string;
  body: string[];
}

interface LegalPageShellProps {
  title: string;
  intro: string;
  sections: LegalSection[];
}

// Shared visual shell for /terms and /privacy — mirrors the existing
// public-page design system (dark gradient + ambient purple blobs, same
// as src/app/contact/page.tsx and the homepage). Both public pages are
// dark-only today with no light-mode toggle, so this intentionally does
// not add one here either.
export default function LegalPageShell({ title, intro, sections }: LegalPageShellProps) {
  const { t } = useLanguage();
  const tl = t.legal;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #04020C 0%, #0A0618 50%, #04020C 100%)' }}>
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full opacity-20" style={{ background: 'radial-gradient(circle, rgba(93,10,244,0.4) 0%, transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.3) 0%, transparent 70%)', filter: 'blur(80px)' }} />
      </div>

      <nav className="relative z-10 max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link href="/" className="flex items-center">
          <Image
            src="/assets/images/LOGO_REZERVO_HORIZONTALE-removebg-preview-1785868391038.png"
            alt="Rezervo Online Logo"
            width={200}
            height={50}
            className="h-11 w-auto object-contain"
          />
        </Link>
        <LanguageSwitcher />
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-6 pb-20">
        <header className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{title}</h1>
          <p className="text-[#8A7FB0] text-xs mb-3">{tl.lastUpdated}</p>
          <p className="text-[#B3A9D6] text-base leading-relaxed max-w-2xl">{intro}</p>
        </header>

        <div className="space-y-5">
          {sections.map((s) => (
            <section
              key={s.heading}
              className="rounded-2xl p-6 md:p-7"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(124,58,237,0.15)' }}
            >
              <h2 className="text-lg font-semibold text-white mb-3">{s.heading}</h2>
              <div className="space-y-2.5">
                {s.body.map((p, i) => (
                  <p key={i} className="text-[#B3A9D6] text-sm leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}

          <section
            className="rounded-2xl p-6 md:p-7"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(124,58,237,0.15)' }}
          >
            <h2 className="text-lg font-semibold text-white mb-3">{tl.contactHeading}</h2>
            <p className="text-[#B3A9D6] text-sm leading-relaxed mb-2">{tl.contactIntro}</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-[#A78BFA] text-sm font-medium hover:text-white transition-colors duration-200"
            >
              {CONTACT_EMAIL}
            </a>
            {(LEGAL_ENTITY_NAME || LEGAL_ENTITY_NIPT || LEGAL_ENTITY_ADDRESS) && (
              <div
                className="mt-3 pt-3 text-[#8A7FB0] text-xs space-y-0.5"
                style={{ borderTop: '1px solid rgba(124,58,237,0.12)' }}
              >
                {LEGAL_ENTITY_NAME && <p>{LEGAL_ENTITY_NAME}</p>}
                {LEGAL_ENTITY_NIPT && <p>NIPT: {LEGAL_ENTITY_NIPT}</p>}
                {LEGAL_ENTITY_ADDRESS && <p>{LEGAL_ENTITY_ADDRESS}</p>}
              </div>
            )}
          </section>
        </div>

        <div className="mt-12 text-center">
          <Link href="/" className="text-[#6B6080] hover:text-[#A89EC4] text-sm transition-colors duration-200">
            ← {t.booking.backToHome}
          </Link>
        </div>
      </main>
    </div>
  );
}
